import type { JSX } from 'solid-js'

export const REVEAL_SCROLLER_ATTR = 'data-reveal-scroller'

const FOCUS_RING_ROOM = 4

const scrolls = (element: HTMLElement) => {
  if (element.hasAttribute(REVEAL_SCROLLER_ATTR)) {
    return true
  }
  const { overflowY } = getComputedStyle(element)
  return (
    (overflowY === 'auto' || overflowY === 'scroll') && element.scrollHeight > element.clientHeight
  )
}

const scrollerOf = (host: HTMLElement, target: HTMLElement) => {
  for (let element = target.parentElement; element; element = element.parentElement) {
    if (scrolls(element)) {
      return element
    }
    if (element === host) {
      return null
    }
  }
  return null
}

const scrollIntoView = (scroller: HTMLElement, target: HTMLElement, room: number) => {
  const label = target.closest('label')
  const view = scroller.getBoundingClientRect()
  const box = (label && scroller.contains(label) ? label : target).getBoundingClientRect()
  const hiddenAbove = view.top - (box.top - room)
  const hiddenBelow = box.bottom + room - view.bottom
  if (hiddenAbove > 0 || box.height + 2 * room > view.height) {
    scroller.scrollTop -= hiddenAbove
  } else if (hiddenBelow > 0) {
    scroller.scrollTop += hiddenBelow
  }
}

// Kobalte's focus trap wraps Tab and Shift+Tab through a sentinel and moves
// focus with focusWithoutScrolling, which can leave it on a control scrolled
// out of view; where preventScroll is unsupported it restores the scroll
// after focusing, so the reveal waits for a microtask. Solid delegates
// focusin through portals, so focus inside a menu or a nested dialog opened
// from the host reaches this handler too and must be left alone.
export const createWrappedFocusReveal =
  (room = FOCUS_RING_ROOM): JSX.EventHandler<HTMLElement, FocusEvent> =>
  (event) => {
    const host = event.currentTarget
    const target = event.target
    const from = event.relatedTarget
    if (
      !(target instanceof HTMLElement) ||
      !host.contains(target) ||
      !(from instanceof HTMLElement) ||
      !from.hasAttribute('data-focus-trap')
    ) {
      return
    }
    queueMicrotask(() => {
      if (target.ownerDocument.activeElement !== target) {
        return
      }
      const scroller = scrollerOf(host, target)
      if (scroller) {
        scrollIntoView(scroller, target, room)
      }
    })
  }
